import React from 'react';
import { FaExclamationTriangle } from 'react-icons/fa';
import { Input, Select } from '../AdminForm';
import { ReadOnlyField } from '../FormComponents';
import { FormFieldRendererProps } from './GenericFormTypes';
import { getFieldIcon } from './FormIconUtils';
import { useFieldFormatting } from './useFieldFormatting';

/**
 * Form Field Renderer Component
 * Renders individual form fields with validation and formatting
 */

export const FormFieldRenderer = <T,>({
  field,
  data,
  onFieldChange,
  validationError,
  onClearError,
}: FormFieldRendererProps<T>) => {
  const { formatDisplayValue, formatInputValue, isValidInput } = useFieldFormatting();

  const value = (data as any)[field.key] || '';
  const icon = getFieldIcon(field.key);
  const hasError = Boolean(validationError);

  // Handle field value change with specific formatting
  const handleFieldChange = (newValue: string) => {
    // Check if input is valid before processing
    if (!isValidInput(newValue, field.key)) {
      return; // Don't update if input is invalid
    }

    const processedValue = formatInputValue(newValue, field.key);
    onFieldChange(field.key as keyof T, processedValue);

    // Clear validation error when user starts typing
    if (hasError) {
      onClearError(field.key);
    }
  };

  // Handle select field changes
  const handleSelectChange = (newValue: string) => {
    onFieldChange(field.key as keyof T, newValue);

    // Clear validation error when user selects an option
    if (hasError) {
      onClearError(field.key);
    }
  };

  // Render field element based on type
  const renderFieldElement = () => {
    if (field.disabled) {
      // Salt okunur alanlar için
      return (
        <ReadOnlyField
          label={field.label}
          value={value}
        />
      );
    }
    if (field.type === 'select' && field.options) {
      return (
        <Select
          key={field.key}
          label={field.label}
          value={value}
          onChange={handleSelectChange}
          options={field.options}
          icon={icon}
          disabled={field.disabled || (field.key === 'office' && !(data as any).management)}
        />
      );
    }

    return (
      <Input
        key={field.key}
        label={field.label}
        value={formatDisplayValue(value, field.key)}
        onChange={handleFieldChange}
        placeholder={getPlaceholder()}
        type={field.type as 'text' | 'email' | 'tel' | 'number' | 'password'}
        icon={icon}
        disabled={field.disabled}
      />
    );
  };

  // Get appropriate placeholder for field
  const getPlaceholder = (): string => {
    if (field.key === 'phone_number') {
      return '5XX XXX XX XX';
    }
    if (field.key === 'identification_number') {
      return '12345678901';
    }
    return field.placeholder || field.label;
  };

  return (
    <div key={field.key}>
      {renderFieldElement()}
      {hasError && (
        <div className="flex items-center gap-2 mt-2 text-red-600 text-sm">
          <FaExclamationTriangle className="text-xs" />
          <span>{validationError}</span>
        </div>
      )}
    </div>
  );
};
